import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["list", "item", "position", "badge", "status"]
  static values = {
    autosave: Boolean
  }

  connect() {
    this.draggedItem = null
    this.placeholder = null
    this.previousItemStyle = null
    this.dragOffsetX = 0
    this.dragOffsetY = 0
    this.saveTimeout = null
    this.boundMove = this.moveDrag.bind(this)
    this.boundEnd = this.endDrag.bind(this)
    this.updatePositions()
  }

  disconnect() {
    this.removeDragListeners()
    this.resetDraggedItem()
    this.removePlaceholder()
  }

  startDrag(event) {
    if (event.button !== undefined && event.button !== 0) return

    const item = event.currentTarget.closest("[data-dashboard-cards-target='item']")
    if (!item) return

    event.preventDefault()
    this.draggedItem = item
    const box = item.getBoundingClientRect()
    this.dragOffsetX = event.clientX - box.left
    this.dragOffsetY = event.clientY - box.top
    this.previousItemStyle = item.getAttribute("style")
    this.placeholder = this.createPlaceholder(item, box)
    this.listTarget.insertBefore(this.placeholder, item.nextSibling)

    this.positionDraggedItem(event.clientX, event.clientY, box)
    this.draggedItem.classList.add("is-dragging")
    this.element.classList.add("is-dragging")

    if (event.currentTarget.setPointerCapture && event.pointerId !== undefined) {
      event.currentTarget.setPointerCapture(event.pointerId)
    }

    document.addEventListener("pointermove", this.boundMove)
    document.addEventListener("pointerup", this.boundEnd)
    document.addEventListener("pointercancel", this.boundEnd)
  }

  moveDrag(event) {
    if (!this.draggedItem) return
    event.preventDefault()
    this.positionDraggedItem(event.clientX, event.clientY)

    const nextItem = this.itemAfterPointer(event.clientX, event.clientY)

    if (nextItem) {
      this.listTarget.insertBefore(this.placeholder, nextItem)
    } else {
      this.listTarget.appendChild(this.placeholder)
    }
  }

  endDrag() {
    if (!this.draggedItem) return

    if (this.placeholder?.parentNode) {
      this.placeholder.parentNode.insertBefore(this.draggedItem, this.placeholder)
    }

    this.resetDraggedItem()
    this.removePlaceholder()
    this.element.classList.remove("is-dragging")
    this.updatePositions()
    this.persistIfNeeded()
    this.removeDragListeners()
  }

  removeDragListeners() {
    this.element.classList.remove("is-dragging")
    document.removeEventListener("pointermove", this.boundMove)
    document.removeEventListener("pointerup", this.boundEnd)
    document.removeEventListener("pointercancel", this.boundEnd)
  }

  createPlaceholder(item, box) {
    const placeholder = document.createElement("div")
    placeholder.className = item.className
    placeholder.classList.remove("is-dragging")
    placeholder.classList.add("dashboard-card-sort-placeholder")
    placeholder.removeAttribute("data-dashboard-cards-target")
    placeholder.removeAttribute("data-card-id")
    placeholder.setAttribute("aria-hidden", "true")
    placeholder.innerHTML = '<div class="dashboard-card-sort-placeholder__inner"></div>'
    placeholder.querySelector(".dashboard-card-sort-placeholder__inner").style.height = `${box.height}px`

    return placeholder
  }

  positionDraggedItem(clientX, clientY, box = this.draggedItem.getBoundingClientRect()) {
    Object.assign(this.draggedItem.style, {
      left: `${clientX - this.dragOffsetX}px`,
      margin: "0",
      position: "fixed",
      top: `${clientY - this.dragOffsetY}px`,
      width: `${box.width}px`,
      zIndex: "1080"
    })
  }

  resetDraggedItem() {
    if (!this.draggedItem) return

    this.draggedItem.classList.remove("is-dragging")

    if (this.previousItemStyle === null) {
      this.draggedItem.removeAttribute("style")
    } else {
      this.draggedItem.setAttribute("style", this.previousItemStyle)
    }

    this.draggedItem = null
    this.previousItemStyle = null
  }

  removePlaceholder() {
    this.placeholder?.remove()
    this.placeholder = null
  }

  itemAfterPointer(pointerX, pointerY) {
    const availableItems = this.orderedItems.filter((item) => item !== this.draggedItem)

    return availableItems.find((item) => {
      const box = item.getBoundingClientRect()
      const middleY = box.top + box.height / 2
      const middleX = box.left + box.width / 2
      const sameRow = pointerY >= box.top && pointerY <= box.bottom

      return pointerY < middleY || (sameRow && pointerX < middleX)
    })
  }

  updatePositions() {
    this.orderedItems.forEach((item, index) => {
      const position = index + 1
      const positionInput = item.querySelector("[data-dashboard-cards-target='position']")
      const badge = item.querySelector("[data-dashboard-cards-target='badge']")

      if (positionInput) positionInput.value = position
      if (badge) badge.textContent = `#${position}`
      this.syncCardPosition(item.dataset.cardId, position)
    })
  }

  syncCardPosition(cardId, position) {
    if (!cardId) return

    document.querySelectorAll(`[data-card-id="${cardId}"]`).forEach((container) => {
      const positionInput = container.querySelector("input[name='company_membership[dashboard_card_preferences][][position]']")
      const badge = container.querySelector(".dashboard-card-position-badge")

      if (positionInput) positionInput.value = position
      if (badge) badge.textContent = `#${position}`
    })
  }

  persistIfNeeded() {
    if (!this.autosaveValue) return

    window.clearTimeout(this.saveTimeout)
    this.saveTimeout = window.setTimeout(() => this.persist(), 250)
  }

  async persist() {
    this.setStatus("Salvando organização...")

    try {
      const response = await fetch(this.element.action, {
        method: this.formMethod,
        credentials: "same-origin",
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": this.csrfToken()
        },
        body: new FormData(this.element)
      })

      const payload = await response.json().catch(() => ({}))

      if (!response.ok || payload.ok === false) {
        throw new Error((payload.errors || ["Não foi possível salvar a organização agora."]).join(" "))
      }

      this.setStatus("Organização salva.")
    } catch (error) {
      this.setStatus(error.message || "Não foi possível salvar a organização agora.", true)
    }
  }

  setStatus(message, isError = false) {
    if (!this.hasStatusTarget) return

    this.statusTarget.textContent = message
    this.statusTarget.classList.toggle("text-danger", isError)
    this.statusTarget.classList.toggle("text-muted", !isError)
  }

  csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.content || ""
  }

  get formMethod() {
    return (this.element.querySelector("input[name='_method']")?.value || this.element.method || "post").toUpperCase()
  }

  get orderedItems() {
    return Array.from(this.listTarget.querySelectorAll("[data-dashboard-cards-target='item']"))
  }
}
